import { RestClient } from "./restClient";

export type SkillTier = "user" | "department" | "company" | "platform";

export interface Skill {
  id: string;
  name: string;
  description: string;
  body: string;
  version: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  source: string;
}

export interface GroupedSkills {
  platform: Skill[];
  company: Skill[];
  department: Skill[];
  user: Skill[];
}

export interface CreateSkillInput {
  name: string;
  description: string;
  body: string;
  tier?: SkillTier;
  dept_id?: number | null;
}

export interface UpdateSkillInput {
  description: string;
  body: string;
  tier?: SkillTier;
}

/** Wraps the /skills HTTP surface so pages and components don't have
 *  to know endpoint shapes. Methods throw RestError on non-2xx. */
export class SkillsApi {
  constructor(private client: RestClient) {}

  list(): Promise<GroupedSkills> {
    return this.client.get<GroupedSkills>("/core/v1/skills");
  }

  get(name: string): Promise<Skill> {
    return this.client.get<Skill>(`/core/v1/skills/${encodeURIComponent(name)}`);
  }

  create(input: CreateSkillInput): Promise<Skill> {
    return this.client.json<Skill>("/core/v1/skills", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  update(name: string, input: UpdateSkillInput): Promise<Skill> {
    return this.client.json<Skill>(`/core/v1/skills/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  async remove(
    name: string,
    tier: SkillTier = "user",
    dept_id?: number | null,
  ): Promise<void> {
    const qs = new URLSearchParams({ tier });
    if (dept_id != null) qs.set("dept_id", String(dept_id));
    await this.client.raw(`/core/v1/skills/${encodeURIComponent(name)}?${qs.toString()}`, {
      method: "DELETE",
    });
  }
}
